import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HardHat, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth, formatApiError } from '../context/AuthContext';

export default function SignIn() {
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/app');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-[#0a0a0c] grid-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#ea9a6f] flex items-center justify-center">
            <HardHat className="w-5 h-5 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <span className="serif text-3xl text-white">Crew<span className="text-[#ea9a6f]">Face</span></span>
        </Link>

        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-8">
          <h1 className="serif text-3xl text-white mb-1">Sign in</h1>
          <p className="text-sm text-[#8a877f] mb-6">Manage your crew's headshots and badges.</p>

          <Button onClick={signInWithGoogle} type="button" data-testid="google-signin-button" variant="outline" className="w-full border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full h-11 mb-5">
            Continue with Google
          </Button>
          <div className="dashed-divider mb-5" />

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[#c9c6bf] text-sm">Work email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sam@company.com" data-testid="signin-email-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[#c9c6bf] text-sm">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#ea9a6f] hover:underline">Forgot password?</Link>
              </div>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signin-password-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="signin-error-message">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="signin-submit-button" className="w-full bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] font-medium rounded-full h-11">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : 'Sign in'}
            </Button>
          </form>

          <div className="dashed-divider my-6" />
          <p className="text-sm text-[#8a877f] text-center">New to CrewFace? <Link to="/sign-up" className="text-[#ea9a6f] hover:underline">Create a free account</Link></p>
        </div>
      </div>
    </div>
  );
}
